/**
 * Secure logging utilities for development
 * Prevents sensitive authentication data from being exposed in console logs
 */

import type { Session } from '@supabase/supabase-js'

/**
 * Check if PII logging is allowed based on environment variables
 * PII logs are disabled in production unless explicitly enabled
 */
function isPiiLoggingAllowed(): boolean {
  return import.meta.env.DEV || import.meta.env.VITE_ENABLE_PII_LOGS === 'true'
}

/**
 * Redact sensitive properties from objects for safe logging
 */
function redactSensitiveData(obj: any, depth = 0, seen = new WeakSet<object>()): any {
  if (depth > 5) return '[Max Depth Reached]'
  if (obj === null || obj === undefined) return obj
  if (typeof obj !== 'object') return obj
  if (seen.has(obj as object)) return '[Circular]'
  seen.add(obj as object)
  if (obj instanceof Date) return obj.toISOString()
  if (Array.isArray(obj)) return obj.map(item => redactSensitiveData(item, depth + 1, seen))
  const redacted: any = {}
  const sensitiveKeys = [
    'access_token',
    'refresh_token',
    'id_token',
    'token',
    'password',
    'secret',
    'key',
    'authorization',
    'bearer'
  ]

  for (const [key, value] of Object.entries(obj)) {
    const keyLower = key.toLowerCase()

    if (sensitiveKeys.some(sensitiveKey => keyLower.includes(sensitiveKey))) {
      if (typeof value === 'string' && value.length > 0) {
        // Completely redact tokens for maximum security
        redacted[key] = '[REDACTED:TOKEN]'
      } else {
        redacted[key] = '[REDACTED]'
      }
    } else {
      redacted[key] = redactSensitiveData(value, depth + 1, seen)
    }
  }

  return redacted
}

/**
 * Safely log session data without exposing sensitive tokens
 * In production, PII is hidden unless VITE_ENABLE_PII_LOGS=true
 */
export function safeSessionLog(session: Session | null, message = 'Session data'): void {
  if (!session) {
    console.log(`${message}: null`)
    return
  }

  if (!isPiiLoggingAllowed()) {
    // Production mode: only log basic session existence and expiry info
    console.log(`${message}: [SESSION_EXISTS - expires_at: ${session.expires_at}]`)
    return
  }

  const safeSession = {
    user: {
      id: session.user?.id,
      email: session.user?.email,
      app_metadata: session.user?.app_metadata,
      user_metadata: {
        // Allowlist only a minimal subset
        name: session.user?.user_metadata?.name ?? undefined,
        avatar_url: session.user?.user_metadata?.avatar_url ? '[REDACTED_URL]' : undefined,
        picture: session.user?.user_metadata?.picture ? '[REDACTED_URL]' : undefined
      },
      created_at: session.user?.created_at,
      updated_at: session.user?.updated_at,
      email_confirmed_at: session.user?.email_confirmed_at
    },
    expires_at: session.expires_at,
    expires_in: session.expires_in,
    token_type: session.token_type,
    // Redact the actual tokens
    access_token: session.access_token ? `${session.access_token.substring(0, 8)}...${session.access_token.substring(session.access_token.length - 4)}` : null,
    refresh_token: session.refresh_token ? `${session.refresh_token.substring(0, 8)}...${session.refresh_token.substring(session.refresh_token.length - 4)}` : null,
    provider_token: session.provider_token ? '[REDACTED]' : null,
    provider_refresh_token: session.provider_refresh_token ? '[REDACTED]' : null
  }

  console.log(`${message}:`, safeSession)
}

/**
 * Generic safe logging for any object that might contain sensitive data
 * In production, PII is hidden unless VITE_ENABLE_PII_LOGS=true
 */
export function safeLog(data: any, message = 'Safe log'): void {
  if (!isPiiLoggingAllowed()) {
    console.log(`${message}: [DATA_REDACTED_IN_PRODUCTION]`)
    return
  }

  const safeData = redactSensitiveData(data)
  console.log(`${message}:`, safeData)
}

/**
 * Safe logging specifically for auth state changes
 * In production, PII is hidden unless VITE_ENABLE_PII_LOGS=true
 */
export function safeAuthStateLog(event: string, session: Session | null): void {
  console.log(`🔐 Auth state change: ${event}`)

  if (!isPiiLoggingAllowed()) {
    if (session) {
      console.log('  Session: [SESSION_EXISTS_IN_PRODUCTION]')
    } else {
      console.log('  Session: null')
    }
    return
  }

  if (session) {
    safeSessionLog(session, '  Session')
  } else {
    console.log('  Session: null')
  }
}

/**
 * Development-only logging that respects production environments
 * Automatically applies PII protection based on environment
 */
export function devLog(message: string, data?: any): void {
  if (import.meta.env.DEV) {
    if (data !== undefined) {
      if (!isPiiLoggingAllowed()) {
        console.log(`🔧 [DEV] ${message}: [DATA_REDACTED]`)
      } else {
        const safeData = redactSensitiveData(data)
        console.log(`🔧 [DEV] ${message}:`, safeData)
      }
    } else {
      console.log(`🔧 [DEV] ${message}`)
    }
  }
}

/**
 * Export types for better TypeScript support
 */
export type SafeLogFunction = typeof safeLog
export type SafeSessionLogFunction = typeof safeSessionLog